import { useState, useEffect, useCallback } from 'react';
import { DeviceMode, DeviceInfo } from '../types';
import { detectIsTv, detectIsTouch, detectScreenCategory } from '../utils/deviceDetection';

const STORAGE_KEY = 'animecat_device_mode';

export function useDeviceMode(): DeviceInfo & {
  setMode: (mode: DeviceMode) => void;
  toggleTvMode: () => void;
} {
  const [mode, setModeState] = useState<DeviceMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY) as DeviceMode;
      if (saved && ['auto', 'tv', 'mobile', 'pc'].includes(saved)) {
        return saved;
      }
    }
    return 'auto';
  });

  const [isTvHardware, setIsTvHardware] = useState<boolean>(false);
  const [screenCategory, setScreenCategory] = useState<'mobile' | 'tablet' | 'pc'>('pc');
  const [isTouch, setIsTouch] = useState<boolean>(false);
  const [isDpadActive, setIsDpadActive] = useState<boolean>(false);

  // Initialize and listen to screen changes & D-pad detection
  useEffect(() => {
    const isTv = detectIsTv();
    const touch = detectIsTouch();
    setIsTvHardware(isTv);
    setIsTouch(touch);
    setScreenCategory(detectScreenCategory());

    const handleResize = () => {
      setScreenCategory(detectScreenCategory());
    };

    // Detect user using directional keys / D-pad
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Select'].includes(e.key)) {
        setIsDpadActive(true);
      }
    };

    const handleMouseMove = () => {
      // If mouse moves, user is actively on PC/pointer
      // We don't disable TV mode if explicitly set to 'tv', but we note pointer activity
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const setMode = useCallback((newMode: DeviceMode) => {
    setModeState(newMode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newMode);
    }
  }, []);

  const toggleTvMode = useCallback(() => {
    setMode(mode === 'tv' ? 'auto' : 'tv');
  }, [mode, setMode]);

  // Compute effective device type
  let effectiveDevice: 'tv' | 'mobile' | 'tablet' | 'pc';
  if (mode === 'tv') {
    effectiveDevice = 'tv';
  } else if (mode === 'mobile') {
    effectiveDevice = 'mobile';
  } else if (mode === 'pc') {
    effectiveDevice = 'pc';
  } else {
    // 'auto' mode
    if (isTvHardware) {
      effectiveDevice = 'tv';
    } else if (screenCategory === 'mobile') {
      effectiveDevice = 'mobile';
    } else if (screenCategory === 'tablet') {
      effectiveDevice = 'tablet';
    } else {
      effectiveDevice = 'pc';
    }
  }

  const isTv = effectiveDevice === 'tv';
  const isMobile = effectiveDevice === 'mobile';
  const isTablet = effectiveDevice === 'tablet';
  const isPc = effectiveDevice === 'pc';

  // Apply root classes for TV overscan and 10-foot scale
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (isTv) {
      root.classList.add('device-tv-mode');
      root.classList.remove('device-mobile-mode', 'device-pc-mode');
    } else if (isMobile) {
      root.classList.add('device-mobile-mode');
      root.classList.remove('device-tv-mode', 'device-pc-mode');
    } else {
      root.classList.add('device-pc-mode');
      root.classList.remove('device-tv-mode', 'device-mobile-mode');
    }
  }, [isTv, isMobile]);

  return {
    mode,
    effectiveDevice,
    isTv,
    isMobile,
    isTablet,
    isPc,
    isTouch,
    isDpadActive: isDpadActive || isTv,
    setMode,
    toggleTvMode
  };
}
